import React from "react";
import {
    PencilSquareIcon,
    PlusIcon,
    PrinterIcon,
    TrashIcon,
} from "@heroicons/react/24/outline";
import Toggle from "../../../components/Toggle";

function formatDate(value) {
    if (!value) return "--";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "--";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
}

function ReportsMobileList({
    reports,
    onOpenReportModal,
    onDeleteReport,
    onPrintReport,
    loadingPrint,
}) {
    if (!reports.length) {
        return (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-8 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-400">
                No se encontraron informes.
            </div>
        );
    }

    return (
        <div className="space-y-3 lg:hidden">
            {reports.map((report) => (
                <div
                    key={report.id}
                    className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950"
                >
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">
                                {report.title}
                            </p>
                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                                {formatDate(report.createdAt)}
                            </p>
                        </div>
                    </div>

                    <div className="mt-4 grid grid-cols-1 gap-3">
                        <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                Creado por
                            </p>
                            <p className="mt-1 text-sm text-slate-700 dark:text-slate-200">
                                {`${report.doctor?.firstName || ""} ${
                                    report.doctor?.lastName || ""
                                }`.trim() || "-"}
                            </p>
                        </div>
                    </div>

                    <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
                        <button
                            type="button"
                            onClick={() => onOpenReportModal(report)}
                            className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                        >
                            <PencilSquareIcon className="h-4 w-4" />
                            Editar
                        </button>
                        <button
                            type="button"
                            onClick={() => onPrintReport(report)}
                            disabled={loadingPrint}
                            className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                        >
                            <PrinterIcon className="h-4 w-4" />
                            Imprimir
                        </button>
                        <button
                            type="button"
                            onClick={() => onDeleteReport(report)}
                            className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-200"
                        >
                            <TrashIcon className="h-4 w-4" />
                            Eliminar
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
}

export default function ReportsTableSection({
    reports,
    onOpenReportModal,
    loading,
    onDeleteReport,
    onPrintReport,
    loadingPrint,
    printWithInsurance,
    setPrintWithInsurance,
}) {
    return (
        <section className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950 sm:p-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                    <h2 className="text-sm font-semibold uppercase tracking-[0.12em] text-slate-600 dark:text-slate-300">
                        Historial de informes
                    </h2>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <div className="flex items-center gap-2 text-black dark:text-white">
                        <Toggle
                            value={printWithInsurance}
                            handleChange={setPrintWithInsurance}
                        />
                        <p className="text-sm font-medium">Imprimir con seguro</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => onOpenReportModal()}
                        className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white"
                    >
                        <PlusIcon className="h-4 w-4" />
                        Generar informe
                    </button>
                </div>
            </div>

            <div className="mt-5 overflow-x-auto">
                <table className="hidden min-w-full divide-y divide-slate-200 lg:table dark:divide-slate-800">
                    <thead className="bg-slate-50 dark:bg-slate-900/50">
                        <tr>
                            <th className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                Fecha
                            </th>
                            <th className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                Creado por
                            </th>
                            <th className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                Titulo
                            </th>
                            <th className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                Acciones
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 bg-white dark:divide-slate-800 dark:bg-slate-950">
                        {loading ? (
                            <tr>
                                <td
                                    colSpan={4}
                                    className="px-6 py-8 text-center text-slate-500 dark:text-slate-400"
                                >
                                    Cargando...
                                </td>
                            </tr>
                        ) : reports.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={4}
                                    className="px-6 py-8 text-center text-slate-500 dark:text-slate-400"
                                >
                                    No se encontraron informes
                                </td>
                            </tr>
                        ) : (
                            reports.map((report) => (
                                <tr key={report.id}>
                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-900 dark:text-slate-100">
                                        {formatDate(report.createdAt)}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-900 dark:text-slate-100">
                                        {`${report.doctor?.firstName || ""} ${
                                            report.doctor?.lastName || ""
                                        }`.trim() || "-"}
                                    </td>
                                    <td className="px-3 py-4 text-sm text-slate-900 dark:text-slate-100">
                                        {report.title}
                                    </td>
                                    <td className="px-3 py-4">
                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                onClick={() => onOpenReportModal(report)}
                                                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                                            >
                                                Editar
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => onPrintReport(report)}
                                                disabled={loadingPrint}
                                                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                                            >
                                                Imprimir
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => onDeleteReport(report)}
                                                className="rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-200"
                                            >
                                                Eliminar
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>

                <ReportsMobileList
                    reports={reports}
                    onOpenReportModal={onOpenReportModal}
                    onDeleteReport={onDeleteReport}
                    onPrintReport={onPrintReport}
                    loadingPrint={loadingPrint}
                />
            </div>
        </section>
    );
}
